import React, { useState } from 'react';
import { Button, Space, Tag, Input, Skeleton, Grid, Tooltip, Popover } from 'antd';
import { EllipsisOutlined, FileTextOutlined, SettingOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PAYMENT_STATUS_COLORS, normalizePaymentStatus } from '../../constants';
import { getPaymentDocumentUrls, getPaymentCheckoutUrl } from '../../BillingServices';
import { useErrorHandler } from '../../../../Hooks/useErrorHandler';
import RowActions from '../../../Shared/RowActions/RowActions';
import AppTable from '../../../Shared/AppTable/AppTable';
import SharedFilterBar from '../../../Shared/Filters/SharedFilterBar';
import { paymentFilterFields } from '../../../Shared/Filters/filterConfig';
import PageToolbar from '../../../Shared/PageToolbar';
import ExportButton from '../../../DataExportImport/ExportButton';
import ImportButton from '../../../DataExportImport/ImportButton';
import { Resource } from '../../../../Constants/socket';

const PaymentHistory = ({
  payments,
  loading,
  pagination,
  onTableChange,
  onViewPaymentDetails,
  chipFilters,
  onChipFiltersChange,
  sortValue,
  onSortChange,
  exportFilters,
  cardStyle
}) => {
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();

  const [loadingDocuments, setLoadingDocuments] = useState({});
  const [continuingCheckout, setContinuingCheckout] = useState({});
  const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);
  const screens = Grid.useBreakpoint();

  // antd Table onChange: (pagination, filters, sorter, extra)
  const handleTableChange = (paginationInfo, filters, sorter) => {
    const updatedFiltersRaw = filters || {};
    // Map UI filter keys to API filter keys
    const filterKeyMapping = {
      status: 'payment_status',
      id: 'id',
    };
    const updatedFilters = Object.keys(updatedFiltersRaw).reduce((acc, key) => {
      const mappedKey = filterKeyMapping[key] || key;
      const value = updatedFiltersRaw[key];
      if (value !== undefined && value !== null && value !== '') {
        acc[mappedKey] = Array.isArray(value) && value.length === 1 ? value[0] : value;
      }
      return acc;
    }, {});
    const fieldMapping = {
      'date': 'created_at',
      'total': 'total',
      'coins': 'coins_amount',
      'status': 'payment_status',
      'id': 'id'
    };
    const mappedSorter = sorter && sorter.field ? { ...sorter, field: fieldMapping[sorter.field] || sorter.field } : sorter;
    onTableChange(paginationInfo, updatedFilters, mappedSorter, '');
  };

  const handleViewDocument = async (paymentId) => {
    try {
      setLoadingDocuments(prev => ({ ...prev, [paymentId]: true }));
      const { data } = await getPaymentDocumentUrls(paymentId);

      if (data && data.invoice_url) {
        window.open(data.invoice_url, '_blank');
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingDocuments(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleContinueCheckout = async (paymentId) => {
    try {
      setContinuingCheckout(prev => ({ ...prev, [paymentId]: true }));
      // Get the checkout URL from the API
      const { data } = await getPaymentCheckoutUrl(paymentId);

      if (data && data.checkout_url) {
        window.open(data.checkout_url, '_blank');
      }
    } catch (error) {
      handleError(error);
    } finally {
      setContinuingCheckout(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const columns = [
    {
      title: t('id'),
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t('search')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <a onClick={() => confirm()}>{t('filter')}</a>
            <a onClick={() => { clearFilters(); setSelectedKeys([]); confirm({ closeDropdown: true }); }}>{t('reset')}</a>
          </Space>
        </div>
      ),
      onFilter: (value, record) => record && record.id && String(record.id).includes(value),
      render: (text) => loading ? <Skeleton.Input active size="small" /> : text,
    },
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      sorter: true,
      render: (text) => loading ? <Skeleton.Input active size="small" /> : text,
    },
    {
      title: t('total'),
      dataIndex: 'total',
      key: 'total',
      sorter: true,
      render: (total) => loading ? <Skeleton.Input active size="small" /> : `${total} $`,
    },
    {
      title: t('coins'),
      dataIndex: 'coins',
      key: 'coins',
      sorter: true,
      render: (text) => loading ? <Skeleton.Input active size="small" /> : text,
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      filters: [
        { text: t('payment_status.created'), value: 'created' },
        { text: t('payment_status.processing'), value: 'processing' },
        { text: t('payment_status.paid'), value: 'paid' },
        { text: t('payment_status.failed'), value: 'failed' },
        { text: t('payment_status.refunded'), value: 'refunded' },
        { text: t('payment_status.partially_refunded'), value: 'partially_refunded' },
        { text: t('payment_status.disputed'), value: 'disputed' },
      ],
      render: (status) => {
        if (loading) return <Skeleton.Input active size="small" />;
        const normalizedStatus = normalizePaymentStatus(status);
        const color = PAYMENT_STATUS_COLORS[normalizedStatus] || PAYMENT_STATUS_COLORS.default;
        const fallback = (normalizedStatus || 'unknown').replace(/_/g, ' ');
        const label = t(`payment_status.${normalizedStatus}`, { defaultValue: fallback.charAt(0).toUpperCase() + fallback.slice(1) });
        return (
          <Tag color={color} style={{ textTransform: 'capitalize' }}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => {
        if (loading) return <Skeleton.Input active size="small" />;

        return (
          <RowActions
            screens={screens}
            primary={{
              label: t('view'),
              icon: <FileTextOutlined />,
              onClick: () => handleViewDocument(record.id),
              disabled: normalizePaymentStatus(record.status) !== 'paid',
              loading: loadingDocuments[record.id],
              tooltip: t('view')
            }}
            items={[
              {
                key: 'continue',
                label: t('continue'),
                icon: <ShoppingCartOutlined />,
                onClick: () => handleContinueCheckout(record.id),
                disabled: normalizePaymentStatus(record.status) !== 'created',
                loading: continuingCheckout[record.id]
              },
              {
                key: 'details',
                label: t('details'),
                icon: <SettingOutlined />,
                onClick: () => onViewPaymentDetails(record)
              }
            ]}
          />
        );
      },
    },
  ];

  // Generate skeleton data if loading
  const skeletonData = loading ? Array(5).fill({}).map((_, index) => ({ key: `skeleton-${index}` })) : payments;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <PageToolbar
        style={cardStyle}
        title={t('payment_history')}
        actions={
          <Popover
            trigger="click"
            placement="bottomRight"
            open={isMoreActionsOpen}
            onOpenChange={setIsMoreActionsOpen}
            content={
              <Space direction="vertical" style={{ minWidth: 140 }} onClickCapture={() => setIsMoreActionsOpen(false)}>
                <ImportButton resource={Resource.TRANSACTION} type="text" size="small" />
                <ExportButton resource={Resource.PAYMENT} filters={exportFilters} type="text" size="small" />
              </Space>
            }
          >
            <Tooltip title={t('more_actions')}>
              <Button type="text" icon={<EllipsisOutlined />} size="medium" />
            </Tooltip>
          </Popover>
        }
        filters={
            <SharedFilterBar
              fields={paymentFilterFields}
              value={chipFilters || []}
              onChange={onChipFiltersChange}
              sortFields={[
                { value: 'id', labelKey: 'id' },
                { value: 'created_at', labelKey: 'date' },
                { value: 'total', labelKey: 'total' },
                { value: 'coins_amount', labelKey: 'coins' },
                { value: 'payment_status', labelKey: 'status' },
                { value: 'updated_at', labelKey: 'updated_at' },
              ]}
              sortValue={sortValue}
              onSortChange={onSortChange}
            />
        }
      >
        <div style={{ overflowX: 'auto' }}>
          <AppTable
            columns={columns}
            dataSource={skeletonData}
            pagination={{ ...pagination }}
            onChange={handleTableChange}
          />
        </div>
      </PageToolbar>

    </Space>
  );
};

export default PaymentHistory; 
